/**
 * Kern-Enums aus Datenmodell § 4.
 * SSoT: docs/datenmodell.md.
 *
 * Diese Enums sind absichtlich als String-Union-Types modelliert, nicht als
 * TypeScript-Enum — bessere Serialisierung in JSON, kompatibel mit Postgres-text.
 */

export const ASSET_CLASSES = ['stock', 'etf', 'crypto', 'commodity_etf'] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export const WATCHLIST_LAYERS = [
  // ====== V5-klassisch (deprecated mit V5.1, bleiben für Rollback erreichbar) ======
  'swing', // Swing-Trading-Kandidaten (4-26 Wochen)
  'day_swing', // Kürzere Opportunitäts-Trades (3-10 Tage) — versteckt seit MM7
  'longterm_build', // Langfristige Aufbauwerte (Qualitätsaktien)
  'dividend', // Dividendenwerte für Akkumulation
  'crypto_accumulation', // Krypto-Akkumulation in Bärenmärkten
  'sector_etf', // Sector-ETFs für Vermögensaufbau
  'open_position', // Aktuell laufende Trades
  // ====== V5.1 neu (User-Direktive 09-05) ======
  'core', // V5.1 Quality + Dividenden-Aristokraten + Index-ETFs
  'tactical', // V5.1 Aktive Trade-Setups
  'theme', // V5.1 Themen (KI/Robotik/Space/Tesla via theme_tags)
  'defensive', // V5.1 Gold/Edelmetalle/Defensive-Sektoren
  'crypto', // V5.1 Krypto separat
  'bestand', // #13 — bestehendes Depot/Sparplan (z. B. Revolut-ETFs); eigene Seite, kein Trading-Score
  'fokus', // Tageskadenz (20.09.2026): offene Positionen ∪ Rolle tactical — Watchlist-Default
] as const;
export type WatchlistLayer = (typeof WATCHLIST_LAYERS)[number];

/**
 * V5.1 — Theme-Sub-Tags (additiv pro Watchlist-Eintrag).
 * Ein Eintrag kann mehrere Tags haben (z.B. NVDA = ai + robotics).
 */
export const THEME_TAGS = [
  'theme_ai',
  'theme_robotics',
  'theme_space',
  'theme_tesla',
  'theme_dividend_aristocrat',
] as const;
export type ThemeTag = (typeof THEME_TAGS)[number];

/**
 * V5.1 — Asset-Subtypes für feinere Score-Logik unterhalb der Asset-Klasse.
 * z.B. asset_class='stock' + asset_subtype='quality' → eigener Score-Schwerpunkt.
 */
export const ASSET_SUBTYPES = [
  'quality', // Hohe ROIC/Margen/niedrige Verschuldung
  'dividend', // Dividenden-fokussiert
  'momentum', // Trend-/Wachstumswert
  'theme', // Themen-Wert (multi-tag)
  'tesla', // Tesla-spezifisch (High-Beta, halbierte Position-Size)
  'gold', // Gold/Edelmetall
  'index_etf', // Breiter Index-ETF
  'sector_etf', // Sektor-ETF (Rotation)
  'crypto_anchor', // BTC/ETH (Anker-Coins)
] as const;
export type AssetSubtype = (typeof ASSET_SUBTYPES)[number];

/**
 * V5.1 — Watchlist-Rolle: orthogonal zum Layer, beschreibt die Bewertungs-Achse.
 * Während `layer` für Tab-Sortierung dient, definiert `watchlist_role` welche
 * Strategien sinnvoll sind.
 */
export const WATCHLIST_ROLES = ['core', 'tactical', 'theme', 'defensive', 'crypto'] as const;
export type WatchlistRole = (typeof WATCHLIST_ROLES)[number];

/**
 * V5.1 — Engine-Version Feature-Flag.
 * 'v5' = klassische Engine (alte Layer, alter Score, alte Backtest-Engine)
 * 'v51' = neue Multi-Score-Architektur, Marktphasen-Engine, Pure-Function-Strategien
 */
export const ENGINE_VERSIONS = ['v5', 'v51'] as const;
export type EngineVersion = (typeof ENGINE_VERSIONS)[number];

export const TIMEFRAMES = [
  'daily',
  'weekly',
  'monthly',
  'intraday_4h',
  'intraday_1h',
] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

/** @deprecated V5-Legacy. Ersetzt durch V51_STRATEGY_IDS in src/lib/v51/types.ts. Bleibt für historische DB-Referenzen erhalten. */
export const STRATEGY_TYPES = [
  'time_series_momentum', // TSMOM Aktien/ETF
  'relative_strength', // RS / ETF-Rotation
  'dual_momentum', // Dual Momentum (ETF)
  'twelve_one_momentum', // 12-1-Momentum (Aktien)
  'stage2_breakout', // Stage-2 Breakout (Weinstein/Minervini)
  'stage2_continuation', // Stage-2 Pullback im intakten Trend
  'donchian_crypto', // Donchian-Channel (Krypto)
  'cross_sectional_crypto', // Cross-Sectional Momentum (Krypto)
  'deep_value_accumulation', // Mean-Reversion / Buy-the-Dip (Langfrist)
  'precious_metal_stage2', // Edelmetall-ETF Stage-2
] as const;
export type StrategyType = (typeof STRATEGY_TYPES)[number];

/** @deprecated V5-Legacy. V5.1 nutzt MultiScoreResult statt einzelner Score-Types. */
export const SCORE_TYPES = [
  'swing_score',
  'trade_plan_quality',
  'monitoring_score',
  'longterm_build_score',
  'crypto_accumulation_score',
  'sector_etf_build_score',
] as const;
export type ScoreType = (typeof SCORE_TYPES)[number];

export const SETUP_TYPES = [
  'breakout', // Ausbruch über Resistance
  'pullback', // Pullback im intakten Trend
  'stage2_entry', // Frühe Stage-2-Übergangs-Entries → MA30W-Stop, NIE ATR
  'continuation', // Trend-Fortsetzung
  'mean_reversion', // Buy-the-Dip-Akkumulation
] as const;
export type SetupType = (typeof SETUP_TYPES)[number];

/**
 * Setup-spezifische Stop-Wahl ist Pflicht.
 * Siehe Memory feedback_atr_stop_kills_early_setups:
 * - ATR funktioniert bei Breakout (+0.29R)
 * - ATR zerstört Stage-2-Entry (-0.34R) und Pullback (-0.21R)
 * - MA30W bei Stage-2-Entries Pflicht
 */
export const STOP_TYPES = [
  'atr_multiple', // Nur bei breakout
  'ma30w_below', // MA30W bei stage2_entry
  'chandelier', // Chandelier-Exit
  'structure_low', // Letztes Swing-Low
  'percent_below', // Fixer Prozentwert
  'no_stop', // Akkumulation hat keinen Stop
] as const;
export type StopType = (typeof STOP_TYPES)[number];

export const MARKET_PHASES = [
  'risk_on_uptrend',
  'risk_on_consolidation',
  'transition',
  'risk_off_correction',
  'risk_off_bear',
] as const;
export type MarketPhase = (typeof MARKET_PHASES)[number];

export const LOADING_STATUSES = [
  'idle',
  'loading',
  'success',
  'stale', // Erfolgreich geladen, aber älter als TTL
  'error',
  'no_data', // Erfolgreich geladen, keine Daten ("Heute kein Signal")
] as const;
export type LoadingStatus = (typeof LOADING_STATUSES)[number];

export const POSITION_SIDES = ['long', 'short'] as const;
export type PositionSide = (typeof POSITION_SIDES)[number];

export const BROKERS = ['ibkr', 'manual', 'revolut', 'binance'] as const;
export type Broker = (typeof BROKERS)[number];

export const POSITION_STATUSES = ['open', 'partially_closed', 'closed'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];

export const TRADE_PLAN_STATUSES = [
  'draft',
  'active',
  'triggered',
  'invalidated',
  'cancelled',
  'expired',
  'closed', // V11.A.1: Endzustand nach Position-Close (Stop, TP3, manueller Close)
] as const;
export type TradePlanStatus = (typeof TRADE_PLAN_STATUSES)[number];

/**
 * V11.B — Order-Typen für Trade-Pläne.
 *
 * Vor V11.B existierte keine Order-Typ-Differenzierung — jeder Plan öffnete
 * sofort, sobald der aktuelle Kurs in die Entry-Zone fiel. Das war für
 * Pullback-Setups falsch (die warten gerade auf den Pullback) und für
 * Breakouts falsch (die warten auf den Ausbruch über das Pivot).
 *
 * - limit_buy   → Position öffnet, wenn current_price ≤ trigger_price.
 *                 Klassisch für Pullback / Mean-Reversion: „Kaufen wenn
 *                 der Kurs zur Entry-Zone runterkommt."
 * - buy_stop    → Position öffnet, wenn current_price ≥ trigger_price.
 *                 Klassisch für Breakout / Stage-2-Entry: „Kaufen wenn
 *                 der Kurs über das Pivot ausbricht."
 * - market      → Position öffnet sofort, sobald der Plan aktiv ist.
 *                 Für Akkumulations-Tranchen und Trend-Folger nach
 *                 Re-Ranking (Dual-Momentum etc.).
 */
export const ORDER_TYPES = ['limit_buy', 'buy_stop', 'market'] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const EXIT_REASONS = [
  'tp1_hit',
  'tp2_hit',
  'tp3_hit',
  'stop_hit',
  'manual_close',
  'invalidation',
  // M5 (2026-05-14) — Time-Stop: Position überschreitet max_hold_days
  // und ist nicht in Profit (current_r < 1). Bei Profit weiterlaufen lassen.
  'time_stop',
] as const;
export type ExitReason = (typeof EXIT_REASONS)[number];

export const JOURNAL_ACTION_TYPES = [
  'entry',
  'exit',
  'adjustment',
  'observation',
  'review_weekly',
  'review_monthly',
] as const;
export type JournalActionType = (typeof JOURNAL_ACTION_TYPES)[number];

export const EMOTIONAL_STATES = [
  'calm',
  'fomo',
  'fear',
  'overconfident',
  'frustrated',
] as const;
export type EmotionalState = (typeof EMOTIONAL_STATES)[number];

export const DISCIPLINE_ASSESSMENTS = [
  'plan_followed',
  'minor_deviation',
  'major_deviation',
  'plan_abandoned',
] as const;
export type DisciplineAssessment = (typeof DISCIPLINE_ASSESSMENTS)[number];

export const ROOT_CAUSE_TYPES = [
  'strategy_weakness',
  'execution_weakness',
  'unforeseeable',
] as const;
export type RootCauseType = (typeof ROOT_CAUSE_TYPES)[number];

export const LESSONS_PERIODS = ['week', 'month', 'quarter', 'ad_hoc'] as const;
export type LessonsPeriod = (typeof LESSONS_PERIODS)[number];

export const LESSONS_CATEGORIES = [
  'process',
  'strategy',
  'discipline',
  'market_read',
  'risk_management',
] as const;
export type LessonsCategory = (typeof LESSONS_CATEGORIES)[number];

export const BOTTOM_LINE_VALUES = ['hot', 'warm', 'neutral', 'cool', 'avoid'] as const;
export type BottomLineValue = (typeof BOTTOM_LINE_VALUES)[number];

export const HIT_RATE_OUTCOMES = [
  'correct',
  'partially_correct',
  'wrong',
  'too_early',
] as const;
export type HitRateOutcome = (typeof HIT_RATE_OUTCOMES)[number];
